import {
  FURNISH_RATES,
  PALETTE,
  clampToSpace,
  placePose,
  type GrabRefusal,
  type Material,
  type PiecePose,
  type PlacementSpace,
} from '@vrlearning/lesson-schema';

/**
 * The furnish step's rules (ADR-037; docs/superpowers/specs/2026-09-30-villa-furnishing-design.md,
 * "Grabbing, turning, colouring"): where a carried piece stands, how the thumbstick turns it, when a
 * hold is worth sending, and how another pupil's piece is drawn between the poses that arrive. Pure,
 * so the numbers are held by tests; render/furniture/interaction.ts applies them.
 *
 * Angles are degrees about +y, counter-clockwise seen from above (a piece's `turn`, three's
 * rotation.y); positions are the villa's metres, [x, z] on the floor.
 */

type Vec3 = readonly [number, number, number];
type Ray = { readonly origin: Vec3; readonly direction: Vec3 };

/** How near and how far from the pupil's eyes a carried piece stands, metres along the floor. */
export const CARRY = { near: 0.8, far: 6 } as const;

/** The thumbstick: beyond this it turns a piece, at this many degrees a second. */
export const TURN = { dead: 0.5, rate: 90 } as const;

/** The palette flick: past `arm`, then back within `release`. */
export const FLICK = { arm: 0.7, release: 0.3 } as const;

/** A hold is sent when the piece moved this far or turned this much since the last one. */
export const HOLD_STEP = { metres: 0.01, degrees: 1 } as const;

/** After this long with no new pose, another pupil's piece is not slid across the gap. */
export const MOVE_GAP_MS = 300;
/** A piece whose hold ended eases to where the server has it over this long. */
export const SETTLE_MS = 150;

/**
 * Where a carried piece's footprint stands: where the ray meets the floor, its distance from the
 * eyes (along the floor) kept within 0.8–6 m -- a ray aimed above the horizon keeps 6 m along its
 * direction -- and then clamped onto the space's floors (the villa's walkable floor, or a package's).
 */
export function carryPoint(
  ray: Ray,
  eye: Vec3,
  space: PlacementSpace,
  size?: readonly number[],
): [number, number] {
  const [ox, oy, oz] = ray.origin;
  const [dx, dy, dz] = ray.direction;
  const flat = Math.hypot(dx, dz);
  let px = ox;
  let pz = oz;
  if (dy < 0) {
    const t = -oy / dy;
    px = ox + dx * t;
    pz = oz + dz * t;
  } else if (flat > 1e-9) {
    px = eye[0] + (dx / flat) * CARRY.far;
    pz = eye[2] + (dz / flat) * CARRY.far;
  }
  let vx = px - eye[0];
  let vz = pz - eye[2];
  let distance = Math.hypot(vx, vz);
  if (distance < 1e-9) {
    // Straight down at the feet: out along where the ray leans, or ahead.
    if (flat > 1e-9) {
      vx = dx / flat;
      vz = dz / flat;
    } else {
      vx = 0;
      vz = -1;
    }
    distance = 1;
  }
  const kept = Math.min(Math.max(distance, CARRY.near), CARRY.far);
  return clampToSpace(
    space,
    [eye[0] + (vx / distance) * kept, eye[2] + (vz / distance) * kept],
    size,
  );
}

/**
 * Where a carried piece goes (decision 7): the first the ray meets of a table top (within its area, its
 * piece fitting), a wall face (between its low and high, the piece hung flat, centred on the hit), or the
 * floor (carryPoint). A hung piece faces away from the wall (`turn`, degrees, three's rotation.y).
 */
export function carryTarget(
  ray: Ray,
  eye: Vec3,
  space: PlacementSpace,
  size: readonly [number, number, number],
): { at: [number, number]; y: number; turn?: number } {
  const [ox, oy, oz] = ray.origin;
  const [dx, dy, dz] = ray.direction;
  let best: { t: number; place: { at: [number, number]; y: number; turn?: number } } | undefined;
  for (const table of space.tables) {
    if (dy >= 0 || oy <= table.height) continue;
    const t = (table.height - oy) / dy;
    const x = ox + dx * t;
    const z = oz + dz * t;
    if (Math.hypot(x - eye[0], z - eye[2]) > CARRY.far) continue;
    const placed = placePose(space, { at: [x, z], y: table.height }, size);
    if (placed.y === table.height && (!best || t < best.t)) best = { t, place: placed };
  }
  for (const wall of space.walls) {
    const [ax, az] = wall.from;
    const ex = wall.to[0] - ax;
    const ez = wall.to[1] - az;
    // The wall's vertical plane: n·(p − a) = 0 with n = (−ez, ex).
    const nx = -ez;
    const nz = ex;
    const denominator = nx * dx + nz * dz;
    if (Math.abs(denominator) < 1e-9) continue;
    const t = (nx * (ax - ox) + nz * (az - oz)) / denominator;
    if (t <= 0) continue;
    const x = ox + dx * t;
    const y = oy + dy * t;
    const z = oz + dz * t;
    const along = ((x - ax) * ex + (z - az) * ez) / (ex * ex + ez * ez);
    if (along < 0 || along > 1 || y < wall.low || y > wall.high || t > CARRY.far) continue;
    // Hung centred on the hit: its bottom half its height lower; just in front, on the pupil's side.
    const front = (ox - x) * nx + (oz - z) * nz > 0 ? 1 : -1;
    const length = Math.hypot(nx, nz);
    const placed = placePose(
      space,
      {
        at: [x + (front * nx * 0.1) / length, z + (front * nz * 0.1) / length],
        y: Math.max(0.02, y - size[1] / 2),
      },
      size,
    );
    if (placed.y > 0 && (!best || t < best.t)) {
      const turn = normaliseTurn((Math.atan2(front * nx, front * nz) * 180) / Math.PI);
      best = { t, place: { ...placed, turn } };
    }
  }
  if (best) return best.place;
  return { at: carryPoint(ray, eye, space, size), y: 0 };
}

/** Degrees in 0–360. */
export function normaliseTurn(turn: number): number {
  const t = ((turn % 360) + 360) % 360;
  return t === 360 ? 0 : t;
}

/** One frame of the holding thumbstick: beyond ±0.5 it turns the piece at 90° a second, right clockwise. */
export function turnBy(turn: number, axisX: number, dt: number): number {
  if (Math.abs(axisX) <= TURN.dead) return turn;
  return normaliseTurn(turn - Math.sign(axisX) * TURN.rate * dt);
}

/** Let go of the thumbstick: the nearest 15°. */
export function settleTurn(turn: number): number {
  const step = FURNISH_RATES.turnSnapDegrees;
  return normaliseTurn(Math.round(normaliseTurn(turn) / step) * step);
}

/** From `from` to `to` the shorter way round, degrees, −180 to 180. */
export function shortestTurn(from: number, to: number): number {
  const d = normaliseTurn(to - from);
  return d > 180 ? d - 360 : d;
}

/**
 * The holding thumbstick flicked up or down steps through the palette: past 0.7, then back within
 * 0.3, once. Up (forward, a negative y on the xr-standard mapping) is the next swatch.
 */
export class PaletteFlick {
  #armed: -1 | 0 | 1 = 0;

  /** The thumbstick's y this frame; −1, 0 or +1 swatches to step. */
  update(axisY: number): -1 | 0 | 1 {
    if (axisY <= -FLICK.arm) this.#armed = 1;
    else if (axisY >= FLICK.arm) this.#armed = -1;
    else if (Math.abs(axisY) <= FLICK.release && this.#armed !== 0) {
      const step = this.#armed;
      this.#armed = 0;
      return step;
    }
    return 0;
  }
}

/**
 * When a holder sends `device.hold`: at most 10 times a second, and then only when the piece moved
 * 1 cm, turned 1° or changed its material; and at least every 2 s while it is held still, so the
 * server's 10 s idle rule never ends a hold that is only resting.
 */
export class HoldSender {
  #last: { pose: PiecePose; at: number } | undefined;

  due(pose: PiecePose, now: number): boolean {
    const last = this.#last;
    if (!last) return true;
    const since = now - last.at;
    if (since >= FURNISH_RATES.holdKeepaliveMs) return true;
    if (since < 1000 / FURNISH_RATES.holdHz) return false;
    return (
      Math.hypot(pose.at[0] - last.pose.at[0], pose.at[1] - last.pose.at[1]) >=
        HOLD_STEP.metres - 1e-9 ||
      Math.abs(shortestTurn(last.pose.turn, pose.turn)) >= HOLD_STEP.degrees - 1e-9 ||
      pose.material !== last.pose.material
    );
  }

  sent(pose: PiecePose, now: number): void {
    this.#last = { pose, at: now };
  }

  reset(): void {
    this.#last = undefined;
  }
}

const lerpPose = (a: PiecePose, b: PiecePose, t: number): PiecePose => {
  const y = (a.y ?? 0) + ((b.y ?? 0) - (a.y ?? 0)) * t;
  return {
    at: [a.at[0] + (b.at[0] - a.at[0]) * t, a.at[1] + (b.at[1] - a.at[1]) * t],
    turn: normaliseTurn(a.turn + shortestTurn(a.turn, b.turn) * t),
    material: b.material,
    // Only a raised piece has a height: a pose on the floor reads as it always did.
    ...(y > 0 ? { y } : {}),
  };
};

/**
 * Another pupil's piece, from the `session.moves` that arrive: drawn 120 ms behind the newest pose,
 * linear between two for its place, the shorter way round for its turn; past the newest it stays
 * there, and across a gap of more than 300 ms it waits at the earlier pose rather than slide. A hold
 * that ended eases 0.15 s to where the server has the piece.
 */
export class PoseBuffer {
  #samples: { pose: PiecePose; at: number }[] = [];
  #settle: { from: PiecePose; to: PiecePose; at: number } | undefined;
  #drawn: PiecePose | undefined;

  push(pose: PiecePose, now: number): void {
    this.#settle = undefined;
    this.#samples.push({ pose, at: now });
    // Only what the delay can still reach, and the one before it.
    const horizon = now - FURNISH_RATES.interpolationMs - MOVE_GAP_MS;
    while (this.#samples.length > 2 && this.#samples[1]!.at < horizon) this.#samples.shift();
  }

  /** Eases from where the piece is drawn now to `pose`, which it then keeps. */
  settle(pose: PiecePose, now: number): void {
    const from = this.#drawn ?? this.sample(now) ?? pose;
    this.#samples = [{ pose, at: now }];
    this.#settle = { from, to: pose, at: now };
  }

  sample(now: number): PiecePose | undefined {
    const settle = this.#settle;
    if (settle) {
      const t = Math.min(1, Math.max(0, (now - settle.at) / SETTLE_MS));
      const eased = t * t * (3 - 2 * t);
      this.#drawn = t >= 1 ? settle.to : lerpPose(settle.from, settle.to, eased);
      return this.#drawn;
    }
    const samples = this.#samples;
    if (samples.length === 0) return undefined;
    const at = now - FURNISH_RATES.interpolationMs;
    const newest = samples[samples.length - 1]!;
    if (at >= newest.at || samples.length === 1) {
      this.#drawn = newest.pose;
      return newest.pose;
    }
    if (at <= samples[0]!.at) {
      this.#drawn = samples[0]!.pose;
      return samples[0]!.pose;
    }
    for (let i = samples.length - 1; i > 0; i--) {
      const a = samples[i - 1]!;
      const b = samples[i]!;
      if (at < a.at) continue;
      if (b.at - a.at > MOVE_GAP_MS) {
        this.#drawn = a.pose;
        return a.pose;
      }
      this.#drawn = lerpPose(a.pose, b.pose, (at - a.at) / (b.at - a.at));
      return this.#drawn;
    }
    this.#drawn = newest.pose;
    return newest.pose;
  }

  clear(): void {
    this.#samples = [];
    this.#settle = undefined;
    this.#drawn = undefined;
  }
}

/** Where the rig stands: its floor point and its turn about y (radians, three's rotation.y). */
export type RigPlace = { readonly at: readonly [number, number]; readonly yaw: number };

/** A world offset turned by `yaw` about y, as three turns a vector by rotation.y. */
function turned(x: number, z: number, yaw: number): [number, number] {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return [x * c + z * s, -x * s + z * c];
}

/**
 * A piece carried through a room teleport: it keeps its distance and direction from the rig, and
 * lands in the new room there, clamped onto the floor.
 */
export function teleportCarry(
  at: readonly [number, number],
  from: RigPlace,
  to: RigPlace,
  space: PlacementSpace,
  size?: readonly number[],
): [number, number] {
  const local = turned(at[0] - from.at[0], at[1] - from.at[1], -from.yaw);
  const world = turned(local[0], local[1], to.yaw);
  return clampToSpace(space, [to.at[0] + world[0], to.at[1] + world[1]], size);
}

/** What a key does to a held piece in the desktop preview: Q and E turn by 15°, 1–8 colour it. */
export function keyAction(
  key: string,
): { turn: number } | { material: Exclude<Material, 'plain'> } | undefined {
  const lower = key.toLowerCase();
  if (lower === 'q') return { turn: FURNISH_RATES.turnSnapDegrees };
  if (lower === 'e') return { turn: -FURNISH_RATES.turnSnapDegrees };
  if (/^[1-8]$/.test(key)) return { material: PALETTE[Number(key) - 1]! };
  return undefined;
}

/** What a grab can come back as: granted, one of the server's refusals, or no answer in 2 s. */
export type GrabAnswer = 'granted' | GrabRefusal | 'timeout';

const REFUSALS: Record<Exclude<GrabAnswer, 'granted'>, string> = {
  'piece-taken': 'Iemand anders heeft dit al vast.',
  'villa-locked': 'De villa is op slot.',
  'holding-another': 'Je hebt al iets vast.',
  paused: 'De les staat even stil.',
  'not-furnishing': 'Nu even niet: kijk eerst rond.',
  'no-furniture': 'Dat lukte niet. Probeer het nog eens.',
  'too-big': 'Dit werkstuk is te groot voor deze ruimte.',
  timeout: 'Dat lukte niet. Probeer het nog eens.',
};

/**
 * The plate a refused grab shows for 1.5 s, in Dutch. In another world than the villa the lock is the
 * space's, not the villa's.
 */
export function refusalText(
  answer: Exclude<GrabAnswer, 'granted'>,
  space?: Pick<PlacementSpace, 'world'>,
): string {
  if (answer === 'villa-locked' && space && space.world !== 'villa') return 'De ruimte is op slot.';
  return REFUSALS[answer];
}

/** How long a refusal's plate shows. */
export const REFUSAL_SECONDS = 1.5;

/**
 * The xr-standard thumbstick of an input source: axes 2 and 3 (x right, y down); zeros for a hand
 * or a controller without a gamepad.
 */
export function thumbstickOf(
  source:
    { readonly gamepad?: { readonly axes: readonly number[] } | null | undefined } | undefined,
): [number, number] {
  const axes = source?.gamepad?.axes;
  if (!axes || axes.length < 4) return [0, 0];
  return [axes[2] ?? 0, axes[3] ?? 0];
}
